import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Response_Processing from "../../../../src/layout/Others/Response_Processing";
import AdminSaveHeader from "../../../components/CommonComponents/AdminSaveHeader";
import FormContainer from "../../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../../components/CommonComponents/LeftWrapper";
import MessageHeading from "../../../components/CommonComponents/MessageHeading";
import Wrapper from "../../../components/CommonComponents/Wrapper";
import MessageLabel from "../../../components/CommonComponents/MessageLabel";
import RightWrapper from "../../../components/CommonComponents/RightWrapper";
import { toast } from "react-toastify";
import AdminCustomInput from "../../../components/CommonComponents/AdminCustomInput";
import AdminImageUploadZone from "../../../components/CommonComponents/AdminImageUploadZone";
import { useMyContext } from "../../../components/Provider/CommonProvider";
import AdminBackNavigation from "../../../components/CommonComponents/AdminBackNavigation";
import Defaultloadinggif from "../../../layout/Others/Defaultloadinggif";
import AdminCustomButton from "../../../components/CommonComponents/AdminCustomButton";
import useCompressImageConfig from "../../../components/CommonApis/Compressimageapi";

function RegionAddUpdate({ isModal = false, onSuccess, forceCreate = false }) {

    const baseURL = process.env.REACT_APP_Baseurl;
    const editApi = `${baseURL}/admin/region/edit`;
    const storeApi = `${baseURL}/admin/region/store`;
    const updateApi = `${baseURL}/admin/region/update`;

    const { fetchRequest, globalImageUplaodAPI } = useMyContext();
    const imageApi = globalImageUplaodAPI();
    const { compressResolutionFormat } = useCompressImageConfig();
    const disableRatioValidation = Number(compressResolutionFormat?.disable_image_ratio_validation) === 1;
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id && !forceCreate;
    const combinedRef = useRef({});
    const [formDataError, setFormDataError] = useState({});
    const [formData, setFormData] = useState({ name: "", slug: "" });
    const [showOverlay, setShowOverlay] = useState(false);
    const [withOutRefresh, setWithOutRefresh] = useState(false);
    const [loading, setLoading] = useState(false);
    const [regionImageFile, setRegionImageFile] = useState('');

    const formValidation = () => {
        let formIsValid = true;

        if (
            !formData?.name ||
            formData.name.trim().length < 3 ||
            formData.name.trim().length > 100
        ) {
            setFormDataError((prev) => ({
                ...prev,
                name: !formData?.name
                    ? "Name cannot be empty."
                    : formData.name.trim().length < 3
                        ? "Name must be at least 3 characters."
                        : "Name must not exceed 100 characters.",
            }));
            combinedRef.current['name']?.focus();
            formIsValid = false;
        }

        if (
            !formData?.slug ||
            formData.slug.trim().length < 3 ||
            formData.slug.trim().length > 100
        ) {
            setFormDataError((prev) => ({
                ...prev,
                slug: !formData?.slug
                    ? "Slug cannot be empty."
                    : formData.slug.trim().length < 3
                        ? "Slug must be at least 3 characters."
                        : "Slug must not exceed 100 characters.",
            }));
            combinedRef.current['slug']?.focus();
            formIsValid = false;
        }

        return formIsValid;
    };

    const validateImageRatio = (file, expectedRatio, expectedWidth, expectedHeight) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                const ratio = width / height;
                const expectedRatioPrecision = 0.01;
                const ratioMatch = Math.abs(ratio - expectedRatio) < expectedRatioPrecision;
                const dimensionMatch = (width === expectedWidth && height === expectedHeight);
                if (ratioMatch || dimensionMatch) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            };
            img.onerror = () => reject(new Error('Error loading image'));
        });
    };

    const handleStore = (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (formValidation()) {
            if (!id || forceCreate) {
                handleAdd();
            } else {
                handleUpdate();
            }
        }
    };

    const handleFileUpload = async (responseId) => {
        try {
            const formDataAdd = new FormData();
            const imageFields = [];
            formDataAdd.append('id', responseId);
            formDataAdd.append('modelName', 'Region');
            formDataAdd.append('region_image', regionImageFile);
            imageFields.push("region_image");
            imageFields.forEach((field) => {
                formDataAdd.append('imageFields[]', field);
            });
            await fetchRequest({
                method: 'POST',
                url: `${imageApi}`,
                params: {},
                data: formDataAdd,
            });
            setRegionImageFile(null);
        } catch (err) {
            toast.error(err?.response?.data?.message);
        }
    };

    const handleAdd = async () => {
        setShowOverlay(true);
        try {
            const formDataAdd = { name: formData.name, slug: formData.slug };
            const response = await fetchRequest({
                method: 'POST',
                url: `${storeApi}`,
                params: {},
                data: formDataAdd,
            });
            const result = response;
            if (result?.status) {
                if (regionImageFile) {
                    await handleFileUpload(result?.id);
                }
                setWithOutRefresh(!withOutRefresh);
                toast.success(result?.message);
                if (typeof onSuccess === 'function') {
                    onSuccess(result?.id, formData?.name);
                } else {
                    navigate("/admin-regions");
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.message);
        } finally {
            setShowOverlay(false);
        }
    };

    const handleUpdate = async () => {
        try {
            const formDataAdd = { name: formData.name, slug: formData.slug };
            const response = await fetchRequest({
                method: 'POST',
                url: `${updateApi}/${id}`,
                params: {},
                data: formDataAdd,
            });
            const result = response;
            if (result?.status) {
                if (regionImageFile) {
                    await handleFileUpload(result?.id || id);
                }
                toast.success(result?.message);
                setWithOutRefresh(!withOutRefresh);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message);
        } finally {
            setShowOverlay(false);
        }
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFormDataError((prev) => ({ ...prev, [name]: '' }));
    };

    const handleFileChange = async (event) => {
        const { files, name } = event.target;
        setFormDataError((prev) => ({ ...prev, [name]: "" }));
        const allowedTypes = disableRatioValidation
            ? ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif', 'image/svg+xml']
            : ['image/jpeg', 'image/png', 'image/jpg'];
        if (files?.length > 0) {
            const newFile = files[0];
            if (!allowedTypes.includes(newFile?.type)) {
                setFormDataError((prev) => ({
                    ...prev,
                    [name]: disableRatioValidation
                        ? "Invalid file type."
                        : "Only JPG, JPEG, and PNG files are allowed.",
                }));
                setRegionImageFile(null);
                return;
            }
            // if (!disableRatioValidation) {
            //     const isValidSize = await validateImageRatio(newFile, 1 / 1, 1080, 1080);
            //     if (!isValidSize) {
            //         setFormDataError((prev) => ({
            //             ...prev,
            //             [name]: "Image must be in 1:1 Ratio or 1080x1080px.",
            //         }));
            //         setRegionImageFile(null);
            //         return;
            //     }
            // }
            if (name === 'region') {
                setRegionImageFile(newFile);
            }
        }
    };

    const handleClearSelectFile = (value) => {
        if (value === 'region') {
            setRegionImageFile(null);
        }
    };

    useEffect(async () => {
        if (id && !forceCreate) {
            setLoading(true);
            await fetchRequest({
                method: 'GET',
                url: `${editApi}/${id}`,
                params: {},
                data: {},
            })
                .then(response => {
                    if (response?.status) {
                        setFormData((prev) => ({
                            ...prev,
                            ...response?.Region,
                        }));
                    }
                })
                .catch((error) => {
                    toast.error(error?.response?.data?.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [withOutRefresh]);

    if (loading) {
        return <Defaultloadinggif className={"heightOnLoader"} />;
    }

    return (
        <>
            {showOverlay && <Response_Processing />}
            {!isModal && <AdminBackNavigation path={`/admin-regions`} text="Back" />}
            {!isModal && (
                <AdminSaveHeader heading={isEditing ? formData?.name : "Add Region"} saveText={isEditing ? "Update" : "Save"} saveMethod={handleStore} loading={showOverlay} />
            )}
            <FormContainer>
                <LeftWrapper>
                    <Wrapper>
                        <MessageHeading text={isEditing ? "Edit Region" : "Add Region"} />
                        <MessageLabel text="Name" className="mandatorySimple" />
                        <AdminCustomInput
                            type="text"
                            name="name"
                            value={formData?.name}
                            onChange={handleInput}
                            placeHolder={"Enter region name"}
                            ref={(el) => (combinedRef.current.name = el)}
                            error={formDataError?.name}
                            maxLengthValidation={100}
                        />
                        <MessageLabel text={"Slug"} className="mandatorySimple" />
                        <AdminCustomInput
                            type="text"
                            name="slug"
                            value={formData?.slug}
                            onChange={handleInput}
                            placeHolder={"Enter slug"}
                            ref={(el) => (combinedRef.current.slug = el)}
                            error={formDataError?.slug}
                        />
                    </Wrapper>
                </LeftWrapper>
                <RightWrapper>
                    <Wrapper>
                        <MessageHeading text="Region Image" />
                        <MessageLabel text="Region Image" />
                        <AdminImageUploadZone
                            onChange={handleFileChange}
                            selectedFile={regionImageFile}
                            uploadedImage={formData?.region_image}
                            onDelete={handleClearSelectFile}
                            ref={(el) => (combinedRef.current.region_image = el)}
                            name="region"
                            id={isEditing ? id : undefined}
                            accept="image/jpeg,image/png,image/jpg"
                            error={formDataError?.region}
                        />
                    </Wrapper>
                </RightWrapper>
                {isModal && (
                    <AdminCustomButton
                        id="region-submit-down"
                        className='px-3 py-1 m-0 ms-auto'
                        saveMethod={handleStore}
                        saveText={isEditing ? "Update" : "Save"}
                    />
                )}
            </FormContainer>
        </>
    );
}

export default RegionAddUpdate;
